import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import EarningsHistogram from './EarningsHistogram';
import EarningsDayPanel from './EarningsDayPanel';
import { publicFetch } from '../../utils/authenticatedFetch';
import CONFIG from '../../config/config';

// Self-contained 30-day earnings schedule for the desktop dashboard: the
// histogram plus click-to-view day reporters. days=1&limit=1 keeps the payload
// tiny — the worker always returns the full 30-day histogram regardless.
// With onDaySelect, bar clicks delegate to the host (e.g. App.js loads the day
// into the StockTable; null = deselected) and the highlight is CONTROLLED via
// selectedDay — local state would go stale when the host switches tabs through
// other controls. Without onDaySelect, an inline tile panel opens (local state).
const EarningsSchedulePanel = ({ loggedInUser, onLoginSuccess, onOpenLabelManager, onDaySelect, selectedDay = null }) => {
  const [localDate, setLocalDate] = useState(null);
  const selectedDate = onDaySelect ? selectedDay : localDate;

  const { data } = useQuery({
    queryKey: ['earningsScheduleHistogram'],
    queryFn: async () => {
      const response = await publicFetch(`${CONFIG.API_URLS.EARNINGS_CALENDAR}?days=1&limit=1`);
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const histogram = data?.histogram || [];

  const handleBarClick = useCallback((date) => {
    const next = selectedDate === date ? null : date;
    if (onDaySelect) onDaySelect(next);
    else setLocalDate(next);
  }, [onDaySelect, selectedDate]);

  if (!histogram.length) return null;

  const selectedEntry = selectedDate ? histogram.find(h => h.date === selectedDate) : null;

  return (
    // EarningsHistogram/EarningsDayPanel carry their own 16px horizontal padding
    // (mobile-first design); offset it so the panel aligns with dashboard sections.
    <div style={{ margin: '0 -16px', marginBottom: 8 }}>
      <EarningsHistogram
        histogram={histogram}
        onBarClick={handleBarClick}
        selectedDate={selectedDate}
      />
      {selectedDate && !onDaySelect && (
        <EarningsDayPanel
          date={selectedDate}
          bellwethers={selectedEntry?.bellwethers || []}
          onClose={() => setLocalDate(null)}
          loggedInUser={loggedInUser}
          onLoginSuccess={onLoginSuccess}
          onOpenLabelManager={onOpenLabelManager}
        />
      )}
    </div>
  );
};

export default React.memo(EarningsSchedulePanel);
